import { toNumberFormatNotationShort }      from '@/lib/utils/helper'
import BaseResource                         from '@/lib/data/resource/BaseResource'
import AssetModel                           from '@/api/models/asset/AssetModel'

export default class ALMInvestmentsResource extends BaseResource {
  constructor (payload = {}) {
    super(payload)

    this.TitleValue = payload.investments?.descriptiveStatistics?.marketValue ?? ''
    this.TitleValueChange = payload.investments?.dynamicEvolution?.marketValueChange ?? ''
    this.BaseCurrency = new AssetModel(payload.baseCurrency ?? null)

    // Investments data
    this.Investments = payload.investments ?? {}
    this.DescriptiveStatistics = this.Investments.descriptiveStatistics ?? {}
    this.InvestmentsMaturity = this.Investments.investmentsMaturity ?? {}
    this.Yields = this.Investments.yields ?? {}
    this.TypeAllocation = this.Investments.typeAllocation ?? {}
    this.DynamicEvolution = this.Investments.dynamicEvolution ?? {}
    this.CreditQuality = this.Investments.creditQuality ?? {}
    this.YieldsEvolution = this.Investments.yieldsEvolution ?? {}
  }

  /* PROPERTIES */
  get BaseCurrencyTitle () {
    return this.baseCurrencySymbolSingle() ? this.getBaseCurrencySymbolSingle() : this.getBaseCurrencySymbolSingle() + ' '
  }

  get BaseCurrencyDisplayName () {
    return this.BaseCurrency?.displayName || ''
  }

  get BaseCurrencyDisplaySymbol () {
    return this.BaseCurrency?.displaySymbol || ''
  }

  get TitleValueFmt () {
    return this.TitleValue ? toNumberFormatNotationShort(this.TitleValue) : ''
  }

  get TitleValueChangeFmt () {
    return this.TitleValueChange ? this.BaseCurrencyTitle + toNumberFormatNotationShort(this.TitleValueChange) : ''
  }

  // Descriptive Statistics
  get InvestmentsSize () {
    return this.DescriptiveStatistics.size || 0
  }

  get InvestmentsCount () {
    return this.DescriptiveStatistics.investments || 0
  }

  get InvestmentsMaturityValue () {
    return this.DescriptiveStatistics.maturity || 0
  }

  get InvestmentsMaturityUnit () {
    return this.DescriptiveStatistics.maturityUnit || ''
  }

  get InvestmentsYield () {
    return this.DescriptiveStatistics.yield || 0
  }

  get InvestmentsMarketValue () {
    return this.DescriptiveStatistics.marketValue || 0
  }

  get InvestmentsDuration () {
    return this.DescriptiveStatistics.duration || 0
  }

  get InvestmentsDurationUnit () {
    return this.DescriptiveStatistics.durationUnit || ''
  }

  // Yields
  get YieldsYield () {
    return this.Yields.yield || 0
  }

  get YieldsIncome () {
    return this.Yields.income || 0
  }

  // Type Allocation
  get TypeNames () {
    return this.TypeAllocation.typeNames || []
  }

  get PerTypeAllocationData () {
    return this.TypeAllocation.perTypeAllocationData || []
  }

  get TotalAllocationData () {
    return this.TypeAllocation.totalAllocationData || []
  }

  // Dynamic Evolution
  get EvolutionDates () {
    return this.DynamicEvolution.dates || []
  }

  get EvolutionTypeNames () {
    return this.DynamicEvolution.typeNames || []
  }

  get EvolutionTotalValue () {
    return this.DynamicEvolution.totalValue || 0
  }

  get EvolutionTotalValueChange () {
    return this.DynamicEvolution.totalValueChange || 0
  }

  get EvolutionAmountOfContracts () {
    return this.DynamicEvolution.amountOfContracts || {}
  }

  get EvolutionAmountOfContractsChange () {
    return this.DynamicEvolution.amountOfContractsChange || 0
  }

  get EvolutionDuration () {
    return this.DynamicEvolution.duration || 0
  }

  get EvolutionDurationChange () {
    return this.DynamicEvolution.durationChange || 0
  }

  get EvolutionMarketValue () {
    return this.DynamicEvolution.marketValue || 0
  }

  get EvolutionMarketValueChange () {
    return this.DynamicEvolution.marketValueChange || 0
  }

  // Investments Maturity
  get MaturityLabels () {
    return this.InvestmentsMaturity.labels || []
  }

  get MaturityData () {
    return this.InvestmentsMaturity.maturityData || []
  }

  // Credit Quality
  get CreditQualityRatings () {
    return this.CreditQuality.ratings || []
  }

  get PerRatingContribution () {
    return this.CreditQuality.perRatingContribution || []
  }

  // Yields Evolution
  get YieldsEvolutionDates () {
    return this.YieldsEvolution.dates || []
  }

  get YieldsEvolutionYields () {
    return this.YieldsEvolution.yields || []
  }

  get YieldsEvolutionYieldsChange () {
    return this.YieldsEvolution.yieldsChange || []
  }

  /* METHODS */
  baseCurrencySymbolSingle () {
    return this.BaseCurrencyDisplaySymbol.length === 1
  }

  getBaseCurrencySymbolSingle () {
    return this.baseCurrencySymbolSingle() ? this.BaseCurrencyDisplaySymbol : this.BaseCurrencyDisplayName
  }

  // Helper method to get type allocation by type name
  getTypeAllocationByType (typeName) {
    return this.PerTypeAllocationData.find(item => item.typeName === typeName) || null
  }

  // Helper method to get credit quality data by rating
  getCreditQualityByRating (rating) {
    return this.PerRatingContribution.find(item => item.rating === rating) || null
  }
}
